/**
 * HubAuthGate - Auth Gate component for Hub pages
 * 
 * - Shows login/signup modal for unauthenticated users
 * - Google OAuth is the priority option
 * - After successful login, shows HubWelcomeModal (first time per hub only)
 * - Uses existing Emergent Google Auth
 */

import React, { useState, useEffect, useCallback } from 'react';

import { motion, AnimatePresence } from 'framer-motion';
import { X, LogIn, Sparkles, Shield, ChevronRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { isNativeApp } from '../utils/nativeGoogleAuth';
import { useTranslation } from 'react-i18next';

const HUB_WELCOME_KEY = 'wassla_hub_welcomed_v1';
const HUB_AUTH_DISMISSED_KEY = 'wassla_hub_auth_dismissed_v1';

// v1.0.0 native Android: hide the web Google OAuth flow (works on web only).


// Check if user has seen the welcome modal for a specific hub
function hasSeenHubWelcome(hubId) {
  try {
    const welcomed = JSON.parse(localStorage.getItem(HUB_WELCOME_KEY) || '{}');
    return welcomed[hubId] === true;
  } catch {
    return false;
  }
}

// Mark hub welcome as seen
function markHubWelcomeSeen(hubId) {
  try {
    const welcomed = JSON.parse(localStorage.getItem(HUB_WELCOME_KEY) || '{}');
    welcomed[hubId] = true;
    localStorage.setItem(HUB_WELCOME_KEY, JSON.stringify(welcomed));
  } catch (e) {
    console.error('Error saving hub welcome state:', e);
  }
}

// Check if guest dismissed auth modal for a specific hub
function hasGuestDismissedAuth(hubId) {
  try {
    const dismissed = JSON.parse(localStorage.getItem(HUB_AUTH_DISMISSED_KEY) || '{}');
    return dismissed[hubId] === true;
  } catch {
    return false;
  }
}

// Mark guest auth modal as dismissed for a hub
function markGuestAuthDismissed(hubId) {
  try {
    const dismissed = JSON.parse(localStorage.getItem(HUB_AUTH_DISMISSED_KEY) || '{}');
    dismissed[hubId] = true;
    localStorage.setItem(HUB_AUTH_DISMISSED_KEY, JSON.stringify(dismissed));
  } catch (e) {
    console.error('Error saving hub auth dismissed state:', e);
  }
}

// Hub-specific configurations
const HUB_CONFIG = {
  'student-hub': {
    icon: '🎓',
    en: {
      title: 'Welcome to Student Hub',
      subtitle: 'Your academic marketplace',
      features: [
        'Find textbooks and study materials',
        'Build a professional CV',
        'Connect with fellow students'
      ],
      welcomeTitle: 'You\'re in!',
      welcomeSubtitle: 'Welcome to the Student Hub community'
    },
    ar: {
      title: 'مرحباً بك في قسم الطلاب',
      subtitle: 'سوقك الأكاديمي',
      features: [
        'ابحث عن الكتب والمواد الدراسية',
        'أنشئ سيرة ذاتية احترافية',
        'تواصل مع زملائك الطلاب'
      ],
      welcomeTitle: 'أنت الآن في الداخل!',
      welcomeSubtitle: 'مرحباً بك في مجتمع قسم الطلاب'
    },
    gradient: 'linear-gradient(135deg, #3B82F6 0%, #6366F1 100%)'
  },
  'ladies-hub': {
    icon: '💄',
    en: {
      title: 'Welcome to Ladies Hub',
      subtitle: 'A curated space for women',
      features: [
        'Shop fashion, beauty & accessories',
        'Support women entrepreneurs',
        'Safe, trusted community'
      ],
      welcomeTitle: 'You\'re in!',
      welcomeSubtitle: 'Welcome to the Ladies Hub community'
    },
    ar: {
      title: 'مرحباً بكِ في قسم السيدات',
      subtitle: 'مساحة مخصصة للسيدات',
      features: [
        'تسوقي الأزياء والجمال والإكسسوارات',
        'ادعمي رائدات الأعمال',
        'مجتمع آمن وموثوق'
      ],
      welcomeTitle: 'أنتِ الآن في الداخل!',
      welcomeSubtitle: 'مرحباً بكِ في مجتمع قسم السيدات'
    },
    gradient: 'linear-gradient(135deg, #F59E0B 0%, #FB923C 100%)'
  },
  'farmer-hub': {
    icon: '🌾',
    en: {
      title: 'Welcome to Farmer Hub',
      subtitle: 'Sudan\'s agricultural marketplace',
      features: [
        'Track live crop prices',
        'Buy & sell agricultural products',
        'Connect with farmers across Sudan'
      ],
      welcomeTitle: 'You\'re in!',
      welcomeSubtitle: 'Welcome to the Farmer Hub community'
    },
    ar: {
      title: 'مرحباً بك في مركز المزارعين',
      subtitle: 'السوق الزراعي السوداني',
      features: [
        'تابع أسعار المحاصيل',
        'بيع واشتري المنتجات الزراعية',
        'تواصل مع المزارعين في السودان'
      ],
      welcomeTitle: 'أنت الآن في الداخل!',
      welcomeSubtitle: 'مرحباً بك في مجتمع مركز المزارعين'
    },
    gradient: 'linear-gradient(135deg, #10B981 0%, #34D399 100%)'
  }
};

const AUTH_COPY = {
  en: {
    signInTitle: 'Sign in to continue',
    signInSubtitle: 'Access exclusive hub features',
    googleBtn: 'Continue with Google',
    emailBtn: 'Continue with Email',
    divider: 'or',
    skipBtn: 'Browse as guest',
    footer: 'By continuing, you agree to our Terms of Use'
  },
  ar: {
    signInTitle: 'سجّل الدخول للمتابعة',
    signInSubtitle: 'احصل على ميزات حصرية',
    googleBtn: 'متابعة بحساب Google',
    emailBtn: 'متابعة بالبريد الإلكتروني',
    divider: 'أو',
    skipBtn: 'تصفح كضيف',
    footer: 'بالمتابعة، فإنك توافق على شروط الاستخدام'
  }
};

// Auth Modal Component
function HubAuthModal({ isOpen, onClose, hubId, lang, onAuthenticated }) {
  const isRTL = lang === 'ar';
  const hubConfig = HUB_CONFIG[hubId] || HUB_CONFIG['student-hub'];
  const copy = AUTH_COPY[lang] || AUTH_COPY.en;
  const hubCopy = hubConfig[lang] || hubConfig.en;
  const { beginGoogleOAuth } = useAuth();

  const handleGoogleSignIn = async () => {
    // Store current URL for redirect after auth
    const currentPath = window.location.pathname + window.location.search;
    localStorage.setItem('authReturnUrl', currentPath);

    if (isNativeApp()) {
      // Android: system-browser flow (Google blocks OAuth in WebViews).
      // beginGoogleOAuth raises the "Signing you in…" state before leaving
      // the app so no login UI can flash during the OAuth return.
      try {
        await beginGoogleOAuth();
      } catch (e) {
        toast.error(isRTL
          ? 'تعذّر فتح تسجيل الدخول عبر Google. تحقق من اتصالك بالإنترنت.'
          : 'Could not open Google sign-in. Check your internet connection.');
      }
      return;
    }

    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Wassla-branded Google OAuth (own client) — backend drives the flow
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `${window.location.origin}/api/auth/google/start?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleEmailSignIn = () => {
    // Store current URL for redirect after auth
    const currentPath = window.location.pathname + window.location.search;
    localStorage.setItem('authReturnUrl', currentPath);
    
    // Navigate to email auth page
    window.location.href = '/auth?mode=login';
  };

  // Lock body scroll when modal is open
  useEffect(() => {
    if (!isOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          role="dialog"
          aria-modal="true"
          onClick={onClose}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 99999,
            background: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(6px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
          data-testid="hub-auth-modal-backdrop"
        >
          <motion.div
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, scale: 0.95, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 24 }}
            transition={{ type: 'spring', stiffness: 350, damping: 30 }}
            style={{
              width: 'min(400px, 100%)',
              maxHeight: 'calc(100dvh - 32px)',
              overflow: 'auto',
              background: '#fff',
              borderRadius: 24,
              boxShadow: '0 25px 60px rgba(0, 0, 0, 0.25)',
              position: 'relative',
              direction: isRTL ? 'rtl' : 'ltr',
            }}
            data-testid="hub-auth-modal"
          >
            {/* Close button */}
            <button
              onClick={onClose}
              aria-label="Close"
              style={{
                position: 'absolute',
                top: 16,
                right: isRTL ? 'auto' : 16,
                left: isRTL ? 16 : 'auto',
                width: 36,
                height: 36,
                borderRadius: 12,
                border: '1px solid rgba(0,0,0,0.08)',
                background: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                zIndex: 10,
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              }}
              data-testid="hub-auth-close"
            >
              <X size={18} color="#64748B" />
            </button>

            {/* Header with hub icon */}
            <div style={{ 
              padding: '32px 24px 24px', 
              textAlign: 'center',
              background: hubConfig.gradient,
              borderRadius: '24px 24px 0 0',
            }}>
              <div style={{
                width: 72,
                height: 72,
                margin: '0 auto 16px',
                borderRadius: 20,
                background: 'rgba(255,255,255,0.95)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 36,
                boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
              }}>
                {hubConfig.icon}
              </div>
              <h2 style={{ 
                margin: 0, 
                fontSize: 22, 
                fontWeight: 800, 
                color: '#fff',
                textShadow: '0 2px 8px rgba(0,0,0,0.15)',
              }}>
                {hubCopy.title}
              </h2>
              <p style={{ 
                margin: '8px 0 0', 
                fontSize: 14, 
                color: 'rgba(255,255,255,0.9)',
                fontWeight: 500,
              }}>
                {hubCopy.subtitle}
              </p>
            </div>

            {/* Features list */}
            <div style={{ padding: '20px 24px' }}>
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: 12,
                marginBottom: 20,
              }}>
                {hubCopy.features.map((feature, idx) => (
                  <div 
                    key={idx} 
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 12,
                      fontSize: 14,
                      color: '#374151',
                    }}
                  >
                    <div style={{
                      width: 24,
                      height: 24,
                      borderRadius: 6,
                      background: 'rgba(16, 185, 129, 0.12)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <Sparkles size={12} color="#10B981" />
                    </div>
                    <span>{feature}</span>
                  </div>
                ))}
              </div>

              {/* Sign in section */}
              <div style={{ 
                background: '#F8FAFC', 
                borderRadius: 16, 
                padding: 20,
                marginBottom: 16,
              }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 10, 
                  marginBottom: 16 
                }}>
                  <LogIn size={18} color="#1F6F3A" />
                  <span style={{ 
                    fontWeight: 700, 
                    fontSize: 16, 
                    color: '#1F2937' 
                  }}>
                    {copy.signInTitle}
                  </span>
                </div>
                <p style={{ 
                  margin: '0 0 16px', 
                  fontSize: 13, 
                  color: '#6B7280' 
                }}>
                  {copy.signInSubtitle}
                </p>

                {/* Google Sign In - Primary (native uses system-browser flow) */}
                <>
                <button
                  onClick={handleGoogleSignIn}
                  style={{
                    width: '100%',
                    height: 50,
                    borderRadius: 14,
                    border: '2px solid #E5E7EB',
                    background: '#fff',
                    color: '#1F2937',
                    fontSize: 15,
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 12,
                    marginBottom: 12,
                    transition: 'all 0.2s ease',
                  }}
                  data-testid="hub-auth-google-btn"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  {copy.googleBtn}
                </button>

                {/* Divider */}
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 12, 
                  margin: '12px 0' 
                }}>
                  <div style={{ flex: 1, height: 1, background: '#E5E7EB' }} />
                  <span style={{ fontSize: 12, color: '#9CA3AF', fontWeight: 500 }}>
                    {copy.divider}
                  </span>
                  <div style={{ flex: 1, height: 1, background: '#E5E7EB' }} />
                </div>
                </>

                {/* Email Sign In */}
                <button
                  onClick={handleEmailSignIn}
                  style={{
                    width: '100%',
                    height: 46,
                    borderRadius: 14,
                    border: '1px solid #E5E7EB',
                    background: '#fff',
                    color: '#6B7280',
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 10,
                    transition: 'all 0.2s ease',
                  }}
                  data-testid="hub-auth-email-btn"
                >
                  <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  {copy.emailBtn}
                </button>
              </div>

              {/* Browse as guest */}
              <button
                onClick={onClose}
                style={{
                  width: '100%',
                  height: 44,
                  borderRadius: 12,
                  border: 'none',
                  background: 'transparent',
                  color: '#9CA3AF',
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'color 0.2s ease',
                }}
                data-testid="hub-auth-skip-btn"
              >
                {copy.skipBtn}
              </button>

              {/* Footer */}
              <p style={{ 
                margin: '8px 0 0', 
                fontSize: 11, 
                color: '#9CA3AF', 
                textAlign: 'center',
              }}>
                {copy.footer}
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// Hub Welcome Modal (shown after successful login, first time only)
function HubWelcomeModal({ isOpen, onClose, hubId, lang }) {
  const { t } = useTranslation();
  const isRTL = lang === 'ar';
  const hubConfig = HUB_CONFIG[hubId] || HUB_CONFIG['student-hub'];
  const hubCopy = hubConfig[lang] || hubConfig.en;

  // Lock body scroll when modal is open
  useEffect(() => {
    if (!isOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  const handleContinue = () => {
    markHubWelcomeSeen(hubId);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          role="dialog"
          aria-modal="true"
          onClick={handleContinue}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 99999,
            background: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(6px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
          data-testid="hub-welcome-modal-backdrop"
        >
          <motion.div
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, scale: 0.9, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 24 }}
            transition={{ type: 'spring', stiffness: 350, damping: 30 }}
            style={{
              width: 'min(340px, 100%)',
              background: '#fff',
              borderRadius: 24,
              boxShadow: '0 25px 60px rgba(0, 0, 0, 0.25)',
              overflow: 'hidden',
              direction: isRTL ? 'rtl' : 'ltr',
              textAlign: 'center',
            }}
            data-testid="hub-welcome-modal"
          >
            {/* Success Header */}
            <div style={{ 
              padding: '32px 24px 24px',
              background: hubConfig.gradient,
            }}>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 400, damping: 20, delay: 0.1 }}
                style={{
                  width: 80,
                  height: 80,
                  margin: '0 auto 16px',
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.95)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 40,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                }}
              >
                {hubConfig.icon}
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <h2 style={{ 
                  margin: 0, 
                  fontSize: 24, 
                  fontWeight: 800, 
                  color: '#fff',
                  textShadow: '0 2px 8px rgba(0,0,0,0.15)',
                }}>
                  {hubCopy.welcomeTitle}
                </h2>
                <p style={{ 
                  margin: '8px 0 0', 
                  fontSize: 14, 
                  color: 'rgba(255,255,255,0.9)',
                  fontWeight: 500,
                }}>
                  {hubCopy.welcomeSubtitle}
                </p>
              </motion.div>
            </div>

            {/* Continue Button */}
            <div style={{ padding: '20px 24px 24px' }}>
              <button
                onClick={handleContinue}
                style={{
                  width: '100%',
                  height: 50,
                  borderRadius: 14,
                  border: 'none',
                  background: hubConfig.gradient,
                  color: '#fff',
                  fontSize: 16,
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
                  transition: 'transform 0.2s ease',
                }}
                data-testid="hub-welcome-continue-btn"
              >
                {t('hag.startExploring')}
                <ChevronRight size={18} />
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/**
 * HubAuthGate - Main component to gate hub access
 * 
 * Usage:
 * <HubAuthGate hubId="student-hub" lang={lang}>
 *   {({ showAuthModal, isAuthenticated, showWelcome }) => (
 *     // Render hub content
 *   )}
 * </HubAuthGate>
 * 
 * Or use the hook:
 * const { showAuthModal, isAuthenticated, AuthModal, WelcomeModal } = useHubAuthGate('student-hub', lang);
 */
export function useHubAuthGate(hubId, lang = 'en') {
  const { user, isReady, isLoggedIn } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [checkedWelcome, setCheckedWelcome] = useState(false);

  // Show auth modal for unauthenticated users on first mount
  // But NOT if they already dismissed it for this hub (browse as guest)
  useEffect(() => {
    if (!isReady) return;
    
    if (!isLoggedIn) {
      // Check if guest already dismissed auth for this hub
      if (hasGuestDismissedAuth(hubId)) {
        return; // Don't show again
      }
      // Give a tiny delay to prevent flash
      const timer = setTimeout(() => {
        setShowAuthModal(true);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setShowAuthModal(false);
      
      // Check if we need to show welcome modal (first time per hub)
      if (!checkedWelcome) {
        setCheckedWelcome(true);
        if (!hasSeenHubWelcome(hubId)) {
          setTimeout(() => {
            setShowWelcomeModal(true);
          }, 200);
        }
      }
    }
  }, [isReady, isLoggedIn, hubId, checkedWelcome]);

  const handleAuthClose = useCallback(() => {
    setShowAuthModal(false);
    // Persist the dismissal so it won't show again for this hub
    markGuestAuthDismissed(hubId);
  }, [hubId]);

  const handleWelcomeClose = useCallback(() => {
    setShowWelcomeModal(false);
    markHubWelcomeSeen(hubId);
  }, [hubId]);

  const AuthModalComponent = useCallback(() => (
    <HubAuthModal
      isOpen={showAuthModal}
      onClose={handleAuthClose}
      hubId={hubId}
      lang={lang}
      onAuthenticated={handleAuthClose}
    />
  ), [showAuthModal, handleAuthClose, hubId, lang]);

  const WelcomeModalComponent = useCallback(() => (
    <HubWelcomeModal
      isOpen={showWelcomeModal}
      onClose={handleWelcomeClose}
      hubId={hubId}
      lang={lang}
    />
  ), [showWelcomeModal, handleWelcomeClose, hubId, lang]);

  return {
    isAuthenticated: isLoggedIn,
    isReady,
    showAuthModal,
    setShowAuthModal,
    showWelcomeModal,
    AuthModal: AuthModalComponent,
    WelcomeModal: WelcomeModalComponent,
  };
}

// Default export for component-based usage
export default function HubAuthGate({ hubId, lang = 'en', children }) {
  const hookResult = useHubAuthGate(hubId, lang);
  
  return (
    <>
      {children(hookResult)}
      <hookResult.AuthModal />
      <hookResult.WelcomeModal />
    </>
  );
}
